import { useMemo } from "react";
import { useCurrentEmployee } from "@/api/Employee";
import { categorizeShifts } from "./categorizeShifts";
import {
  toShiftWithFilteredContents,
  type ShiftWithFilteredContents,
} from "./shiftWithFilteredContents";
import { usePatientOrActivityTitleFilter } from "./PlanningContext";
import type { IShiftWithContents } from "@medoma/models/shiftWithContents";

export type ShiftsBySection = {
  currentUsersShifts: ShiftWithFilteredContents[];
  staffedShifts: ShiftWithFilteredContents[];
  notStaffedShifts: ShiftWithFilteredContents[];
};

const splitShiftsByFilterMatch = (
  shifts: IShiftWithContents[],
  filter: string,
): {
  matching: ShiftWithFilteredContents[];
  nonMatching: ShiftWithFilteredContents[];
} => {
  const filterEnrichedShifts = shifts.map((shift) =>
    toShiftWithFilteredContents(shift, filter),
  );
  return {
    matching: filterEnrichedShifts.filter((shift) => shift.containsFilterMatch),
    nonMatching: filterEnrichedShifts.filter(
      (shift) => !shift.containsFilterMatch,
    ),
  };
};

const countShifts = (shiftsBySection: ShiftsBySection) =>
  shiftsBySection.currentUsersShifts.length +
  shiftsBySection.staffedShifts.length +
  shiftsBySection.notStaffedShifts.length;

export const useFilteredShiftsWithContents = (
  relevantShifts: IShiftWithContents[],
) => {
  const { value: filterValue } = usePatientOrActivityTitleFilter();
  const { data: currentUser } = useCurrentEmployee();

  const { matching, nonMatching } = useMemo(() => {
    const categorized = categorizeShifts(relevantShifts, currentUser);
    const currentUsers = splitShiftsByFilterMatch(
      categorized.currentUsersShifts,
      filterValue,
    );
    const staffed = splitShiftsByFilterMatch(
      categorized.staffedShifts,
      filterValue,
    );
    const notStaffed = splitShiftsByFilterMatch(
      categorized.notStaffedShifts,
      filterValue,
    );

    return {
      matching: {
        currentUsersShifts: currentUsers.matching,
        staffedShifts: staffed.matching,
        notStaffedShifts: notStaffed.matching,
      },
      nonMatching: {
        currentUsersShifts: currentUsers.nonMatching,
        staffedShifts: staffed.nonMatching,
        notStaffedShifts: notStaffed.nonMatching,
      },
    };
  }, [relevantShifts, currentUser, filterValue]);

  const isFilterActive = filterValue !== "";

  const noShiftsMatchSearch =
    isFilterActive && relevantShifts.length >= 1 && countShifts(matching) === 0;

  const numberOfNonMatchingShifts = countShifts(nonMatching);

  return {
    matching,
    nonMatching,
    isFilterActive,
    numberOfNonMatchingShifts,
    hasNonMatchingShifts: numberOfNonMatchingShifts >= 1,
    noShiftsMatchSearch,
  };
};
